// pillStatus — single source of truth for status pill tone + label.
//
// Why this file exists:
// Before the AI-forward skin sweep, every screen wrote its own little
// status-color map (CateringScreen STATUS_TONE, ScheduleScreen
// StatusBadge, ReorderSuggestionsScreen tier checks, etc.). The maps
// drifted: 'cancelled' was sometimes red, sometimes neutral; 'completed'
// was emerald in one place and black/55 in another; 'declined' was
// missing entirely from a couple of surfaces. The new design has a
// strict four-color contract — red / amber / green / blue — and pills
// should look identical across the whole app.
//
// Four-color contract:
//   - Red    = problem the manager has to act on:
//              no_show, declined, critical, anomaly, over_target,
//              past_due, recipeMargin > 35%, unpaid, incomplete_expired
//   - Amber  = caution / pending / slipping:
//              called_out, quoted, low_stock, pending, slipping, soon,
//              recipeMargin 30–35%, past_due (subscription)
//   - Green  = healthy / done / paid:
//              confirmed, completed, approved, posted, ok, paid, healthy,
//              live, on-target, recipeMargin ≤ 30%, active
//   - Blue   = informational / in-progress / neutral platform tags:
//              new, draft, planned, syncing, cancelled, instagram,
//              facebook, tiktok
//
// IMPORTANT — `cancelled` is BLUE, not muted gray.
// Scott confirmed the semantics during Plan: "cancelled means we
// stopped, it's an informational state — not a failure." Same color
// semantics as `new` / `draft` / `planned`. (Old behavior: mixed —
// some screens used black/10 muted, some used red, some used amber.
// The new contract treats it as a neutral platform-style tag.)
//
// `completed` is GREEN. (Old behavior: mixed — Catering used black/10
// muted, Schedule used emerald.) The new contract is "done = good."
//
// New status strings should be added to the right table below AND
// classified into one of the four tones. Don't invent a fifth color.

// Domain → status → tone.
// Each domain is intentionally explicit (no shared default map) so a
// future change to e.g. `draft` in marketing doesn't accidentally
// re-color a `draft` invoice somewhere else.
const TONE_TABLE = {
  // Roster / shift assignments (ScheduleScreen, DailyHuddleScreen).
  // 'scheduled' deliberately omitted — we don't render a pill for it
  // (the row already implies scheduled). Falls through to default.
  shift: {
    scheduled:  'green',   // implicit — usually no pill rendered
    called_out: 'amber',
    no_show:    'red',
    completed:  'green',
    cancelled:  'blue',
  },

  // Catering inquiries (CateringScreen).
  catering: {
    new:       'blue',
    quoted:    'amber',
    confirmed: 'green',
    completed: 'green',
    cancelled: 'blue',
    declined:  'red',
  },

  // Marketing draft state machine (MarketingDraftsInbox).
  // 'rejected' isn't surfaced as a pill in the current UI but is
  // included so a future card render gets a sane default.
  draft: {
    draft:    'blue',     // "needs review"
    approved: 'green',
    posted:   'green',
    rejected: 'red',
  },

  // POS sync status (Square + Toast connect sections).
  // 'ok' is healthy, 'syncing' is in-progress (informational blue),
  // 'error' is the failure state.
  syncStatus: {
    ok:      'green',
    syncing: 'blue',
    error:   'red',
  },

  // Reorder tier (ReorderSuggestionsScreen + SuggestReorderModal).
  // Critical = below safety stock, soon = below par, healthy = above par.
  reorderTier: {
    critical: 'red',
    soon:     'amber',
    healthy:  'green',
    low_stock: 'amber',  // alias used in some helpers
  },

  // Promotion lifecycle (PromotionsScreen).
  // 'live' = currently running, 'planned' = future, 'completed' = past,
  // 'cancelled' = abandoned (still informational, not a failure).
  promotion: {
    live:      'green',
    planned:   'blue',
    completed: 'green',
    cancelled: 'blue',
  },

  // Purchase order / invoice / subscription state.
  // Includes Stripe subscription statuses since those render the same
  // pill style on the SubscriptionCard.
  invoice: {
    sent:               'blue',
    received:           'green',
    cancelled:          'blue',
    paid:               'green',
    past_due:           'amber',     // amber per spec — first warning
    unpaid:             'red',       // red — escalated past_due
    active:             'green',
    trialing:           'blue',
    incomplete:         'amber',
    incomplete_expired: 'red',
    canceled:           'blue',      // Stripe spelling
  },

  // Recipe margin tier (RecipeBuildModal + MenuEngineeringCard).
  // Bands are documented at the call site; this domain takes a
  // pre-computed tier string ('healthy' | 'slipping' | 'over').
  recipeMargin: {
    healthy:  'green',     // ≤ 30%
    slipping: 'amber',     // 30–35%
    over:     'red',       // > 35%
  },

  // Variance tier (VarianceScreen + variance.js).
  // Same four colors, but `deep-red` is just visually-louder red —
  // both map to red tone here; the screen still gets to choose its
  // own intra-tone styling for deep-red rows if needed.
  variance: {
    green:      'green',
    amber:      'amber',
    red:        'red',
    'deep-red': 'red',
    anomaly:    'red',     // AnomalyScanModal hits this
    'on-target': 'green',  // ScheduleSuggestModal
    close:       'amber',  // ScheduleSuggestModal
    off:         'red',    // ScheduleSuggestModal
    medium:      'amber',  // AnomalyScanModal severity
    low:         'blue',   // AnomalyScanModal severity
  },

  // First-week onboarding milestone state (FirstWeekMilestonesCard).
  // Complete = green, otherwise informational blue.
  milestone: {
    complete:   'green',
    incomplete: 'blue',
  },
}

// Domain → status → human-readable label (sentence case).
// Same shape as TONE_TABLE; if a status string isn't here, we fall
// back to the raw status with the first letter capitalized so the
// pill still renders something sane.
const LABEL_TABLE = {
  shift: {
    scheduled:  'Scheduled',
    called_out: 'Called out',
    no_show:    'No show',
    completed:  '✓ Done',
    cancelled:  'Cancelled',
  },
  catering: {
    new:       'New',
    quoted:    'Quoted',
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
    declined:  'Declined',
  },
  draft: {
    draft:    'Needs review',
    approved: 'Approved',
    posted:   'Posted',
    rejected: 'Rejected',
  },
  syncStatus: {
    ok:      'Connected',
    syncing: 'Syncing',
    error:   'Error',
  },
  reorderTier: {
    critical:  'Critical',
    soon:      'Order soon',
    healthy:   'Healthy',
    low_stock: 'Low stock',
  },
  promotion: {
    live:      'Live',
    planned:   'Planned',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },
  invoice: {
    sent:               'Sent',
    received:           'Received',
    cancelled:          'Cancelled',
    paid:               'Paid',
    past_due:           'Past due',
    unpaid:             'Unpaid',
    active:             'Active',
    trialing:           'Trialing',
    incomplete:         'Incomplete',
    incomplete_expired: 'Expired',
    canceled:           'Cancelled',
  },
  recipeMargin: {
    healthy:  'On margin',
    slipping: 'Slipping',
    over:     'Over target',
  },
  variance: {
    green:      'On target',
    amber:      'Watch',
    red:        'Over',
    'deep-red': 'Way over',
    anomaly:    'Anomaly',
    'on-target': 'On target',
    close:       'Close',
    off:         'Off',
    medium:      'Medium',
    low:         'Low',
  },
  milestone: {
    complete:   '✓ Done',
    incomplete: 'To do',
  },
}

// Pick the tone for a (domain, status) pair. Falls back to 'blue'
// (informational) for unknown statuses, since a missing pill is
// noisier than a too-quiet pill.
export function toneFor(domain, status) {
  const table = TONE_TABLE[domain]
  if (!table) return 'blue'
  const tone = table[status]
  if (tone) return tone
  return 'blue'
}

// Pick the label for a (domain, status) pair. Falls back to a
// sentence-case version of the raw status (no underscores) so we
// still render something readable rather than `null`.
export function labelFor(domain, status) {
  const table = LABEL_TABLE[domain]
  if (table && table[status]) return table[status]
  if (typeof status !== 'string' || !status) return ''
  const cleaned = status.replace(/_/g, ' ').toLowerCase()
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

// Convenience helper for the recipe-margin domain since the input is a
// numeric percentage rather than a status string. Bands per the
// four-color contract:
//   ≤ 30 → healthy (green)
//   30–35 → slipping (amber)
//   > 35 → over (red)
export function recipeMarginTier(pct) {
  if (pct == null || Number.isNaN(Number(pct))) return 'healthy'
  const n = Number(pct)
  if (n > 35) return 'over'
  if (n >= 30) return 'slipping'
  return 'healthy'
}
